import { useTranslation } from "react-i18next";
import { server } from "src/coreConfig";
import { setProcedureState } from "src/slices/procedures";
import { useDispatch, useSelector } from "src/store";
import { handleDownloadFile } from "src/utils/dataRenders";
import useAsyncRequest from "./useAsyncRequest";

const corporateUrls = {
  json: {
    procedures: `/djstatic/:customerName/procedures.json`,
  },
  stats: {
    get: `${server}/api/v2/corporate/stats/get/`,
  },
  appointment: {
    search: `${server}/api/v2/corporate/appointment/search/`,
    update: `${server}/api/v2/corporate/appointment/update/`,
    validate: `${server}/api/v2/corporate/appointment/validate/`,
    getAvailable: `${server}/api/v2/corporate/appointment/getAvailable/`,
    create: `${server}/api/v2/corporate/appointment/create/`,
  },
  department: {
    add: `${server}/api/v2/corporate/department/add/`,
    delete: `${server}/api/v2/corporate/department/delete/`,
    detail: `${server}/api/v2/corporate/department/detail/`,
    list: `${server}/api/v2/corporate/department/`,
    update: `${server}/api/v2/corporate/department/update/`,
  },
  holiday: {
    add: `${server}/api/v2/corporate/holiday/add/`,
    delete: `${server}/api/v2/corporate/holiday/delete/`,
    detail: `${server}/api/v2/corporate/holiday/detail/`,
    list: `${server}/api/v2/corporate/holiday/`,
    update: `${server}/api/v2/corporate/holiday/update/`,
  },
  office: {
    add: `${server}/api/v2/corporate/office/add/`,
    delete: `${server}/api/v2/corporate/office/delete/`,
    detail: `${server}/api/v2/corporate/office/detail/`,
    list: `${server}/api/v2/corporate/office/`,
    update: `${server}/api/v2/corporate/office/update/`,
  },
  procedure: `${server}/api/v2/corporate/procedure/`,
  procedureAdmin: {
    add: `${server}/api/v2/corporate/procedure/add/`,
    addRequirement: `${server}/api/v2/corporate/procedure/addRequirement/`,
    availableRequirements: `${server}/api/v2/corporate/procedure/availableRequirements/`,
    delete: `${server}/api/v2/corporate/procedure/delete/`,
    deleteRequirement: `${server}/api/v2/corporate/procedure/deleteRequirement/`,
    detail: `${server}/api/v2/corporate/procedure/detail/`,
    list: `${server}/api/v2/corporate/procedure/`,
    update: `${server}/api/v2/corporate/procedure/update/`,
    updateRequirement: `${server}/api/v2/corporate/procedure/updateRequirement/`,
  },
  requirement: {
    add: `${server}/api/v2/corporate/requirement/add/`,
    delete: `${server}/api/v2/corporate/requirement/delete/`,
    list: `${server}/api/v2/corporate/requirement/`,
    update: `${server}/api/v2/corporate/requirement/update/`,
  },
  schedule: {
    add: `${server}/api/v2/corporate/schedule/add/`,
    addDay: `${server}/api/v2/corporate/schedule/addDay/`,
    delete: `${server}/api/v2/corporate/schedule/delete/`,
    deleteDay: `${server}/api/v2/corporate/schedule/deleteDay/`,
    detail: `${server}/api/v2/corporate/schedule/detail/`,
    list: `${server}/api/v2/corporate/schedule/`,
    update: `${server}/api/v2/corporate/schedule/update/`,
    updateDay: `${server}/api/v2/corporate/schedule/updateDay/`,
  },
  staff: {
    list: `${server}/api/v2/corporate/staff/`,
    logs: `${server}/api/v2/corporate/staff/logs/`,
  },
  state: {
    add: `${server}/api/v2/corporate/state/add/`,
    delete: `${server}/api/v2/corporate/state/delete/`,
    detail: `${server}/api/v2/corporate/state/detail/`,
    list: `${server}/api/v2/corporate/state/`,
    update: `${server}/api/v2/corporate/state/update/`,
  },
  user_appointment: `${server}/api/v2/corporate/procedure/userAppointment/`,
  user_procedure: `${server}/api/v2/corporate/procedure/userProcedure/`,
  userProcedure: {
    add: `${server}/api/v2/corporate/userProcedure/add/`,
    adminList: `${server}/api/v2/corporate/userProcedure/admin/`,
    changeState: `${server}/api/v2/corporate/userProcedure/changeState/`,
    delete: `${server}/api/v2/corporate/userProcedure/delete/`,
    detail: `${server}/api/v2/corporate/userProcedure/detail/`,
    list: `${server}/api/v2/corporate/userProcedure/`,
    update: `${server}/api/v2/corporate/userProcedure/update/`,
    updatePayment: `${server}/api/v2/corporate/userProcedure/updatePayment/`,
    updateRequirement: `${server}/api/v2/corporate/userProcedure/updateRequirement/`,
    updateRequirementStatus: `${server}/api/v2/corporate/userProcedure/updateRequirementStatus/`,
    addFile: `${server}/api/v2/corporate/userProcedure/addFile/`,
    deleteFile: `${server}/api/v2/corporate/userProcedure/deleteFile/`,
    downloadFile: `${server}/api/v2/corporate/userProcedure/downloadFile/`,
  },
};
const useCorporate = () => {
  const { currentCustomer } = useSelector((state) => state.customers);
  const { adminMode } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const { api } = useAsyncRequest();
  const { t } = useTranslation();

  const loadProceduresList = async () => {
    try {
      const response = await fetch(
        corporateUrls.json.procedures.replace(
          ":customerName",
          currentCustomer.shortname
        )
      );
      const data = await response.json();
      dispatch(
        setProcedureState({
          procedures: data.procedures,
          departments: data.departments,
          procedureStates: data.states,
          proceduresLoaded: true,
        })
      );
      localStorage.setItem(
        `${currentCustomer.id}_procedures_v`,
        data.version || 1
      );

      return data;
    } catch (error) {
      console.error("Error loading procedures", error);
    }
  };

  const addUserProcedureFile = async (data) => {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });
    formData.append("mode", adminMode ? "admin" : "user");
    formData.append("customer_id", currentCustomer.id);
    try {
      const response = await api(
        corporateUrls.userProcedure.addFile,
        formData,
        {
          "Content-Type": "multipart/form-data",
          responseType: "json",
        },
        t("Subiendo archivo...")
      );
      return response.json();
    } catch (error) {
      console.error("Error adding user procedure file", error);
    }
  };

  const deleteUserProcedureFile = async (data) => {
    try {
      const response = await api(
        corporateUrls.userProcedure.deleteFile,
        {
          ...data,
          mode: adminMode ? "admin" : "user",
          customer_id: currentCustomer.id,
        },
        {},
        t("Eliminando archivo...")
      );
      return response.json();
    } catch (error) {
      console.error("Error deleting user procedure file", error);
    }
  };
  const downloadUserProcedureFile = async (data) => {
    try {
      const response = await api(
        corporateUrls.userProcedure.downloadFile,
        {
          ...data,
          mode: adminMode ? "admin" : "user",
          customer_id: currentCustomer.id,
        },

        {
          "Content-Type": "application/json",
          responseType: "blob",
        },
        t("Descargando archivo...")
      );
      handleDownloadFile(response);
    } catch (error) {
      console.error("Error downloading user procedure file", error);
    }
  };

  const getStats = async (data) => {
    try {
      const response = await api(corporateUrls.stats.get, {
        ...data,
        customer_id: currentCustomer.id,
      });

      if (response.status !== 200) {
        throw new Error("Error fetching procedure stats");
      }
      return response.data.data;
    } catch (error) {
      console.error("Error loading procedure stats", error);
    }
  };

  return {
    loadProceduresList,
    userProcedure: {
      file: {
        add: addUserProcedureFile,
        delete: deleteUserProcedureFile,
        download: downloadUserProcedureFile,
      },
    },
    corporateStats: {
      get: getStats,
    },
  };
};

export default useCorporate;
